<!-- src/views/Home.vue -->
<template>
  <div class="home">
    <v-card class="mb-6">
      <v-card-title class="headline">
        SSDG - Tournament Tracker
      </v-card-title>
      <v-card-text>
        <p>Welcome to the SSDG Tournament Tracker. This app provides real-time updates for SSDG Tournaments.</p>

        <v-alert
            v-if="loading"
            type="info"
            text="Loading competition data..."
            class="my-4"
        ></v-alert>

        <v-alert
            v-else-if="error"
            type="error"
            :text="error"
            class="my-4"
        ></v-alert>

        <v-alert
            v-else-if="!competition"
            type="warning"
            text="No competition data available. Please try again later."
            class="my-4"
        ></v-alert>

        <template v-else>
          <!-- Main competition card -->
          <v-card
              variant="outlined"
              class="my-4"
          >
            <v-card-title>
              <span v-html="formatCompetitionNameWithIcon(competition.Name)"></span>
            </v-card-title>
            <v-card-subtitle>
              <div class="d-flex flex-wrap gap-2">
                <span><v-icon icon="mdi-calendar" size="small" class="mr-1" /> {{ formatDate(competition.Date) }}</span>
                <span><v-icon icon="mdi-clock-outline" size="small" class="mr-1" /> {{ formatTime24Hour(competition.Time) }}</span>
                <span v-if="competition.CourseName"><v-icon icon="mdi-map-marker" size="small" class="mr-1" /> {{ formatCourseName(competition.CourseName) }}</span>
              </div>
            </v-card-subtitle>
            <v-card-text>
              <div v-if="competition.Comment" class="mb-3">
                <strong>Details:</strong> {{ competition.Comment }}
              </div>

              <div v-if="isFutureCompetition" class="text-primary">
                Starts in: {{ timeUntilStart }}
              </div>
              <div v-else-if="competitionStatus === 'not_started'" class="text-warning">
                Competition scheduled for today but not yet started
              </div>
              <div v-else-if="competitionStatus === 'ended'" class="text-info">
                Competition has ended
              </div>
              <div v-else class="text-success">
                Competition in progress
              </div>

              <v-chip-group class="mt-4">
                <v-chip
                    v-for="(count, division) in divisionCounts"
                    :key="division"
                    :text="`${division}: ${count}`"
                    color="primary"
                    variant="outlined"
                    size="small"
                ></v-chip>
              </v-chip-group>
            </v-card-text>
            <v-card-actions>
              <v-btn
                  color="primary"
                  variant="text"
                  :to="`/competition/${competitionId}`"
              >
                View Details
              </v-btn>
              <v-btn
                  color="secondary"
                  variant="text"
                  :to="`/leaderboard/${competitionId}`"
                  prepend-icon="mdi-trophy"
              >
                Live
              </v-btn>
              <v-btn
                  color="info"
                  variant="text"
                  :to="`/groups/${competitionId}`"
                  prepend-icon="mdi-account-group"
              >
                Player Groups
              </v-btn>
            </v-card-actions>
          </v-card>

          <!-- Subcompetitions/Rounds cards -->
          <div v-if="subCompetitions && subCompetitions.length > 0">
            <h3 class="text-h5 mt-6 mb-3">Competition Rounds</h3>

            <v-row>
              <v-col
                  v-for="(round) in subCompetitions"
                  :key="round.ID"
                  cols="12"
                  md="6"
                  lg="4"
              >
                <v-card
                    variant="outlined"
                    class="h-100"
                    :to="`/competition/${competitionId}`"
                    hover
                >
                  <v-card-title>
                    <span v-html="formatCompetitionNameWithIcon(round.Name)"></span>
                  </v-card-title>
                  <v-card-subtitle>
                    <div class="d-flex flex-wrap gap-2">
                      <span><v-icon icon="mdi-calendar" size="small" class="mr-1" /> {{ formatDate(round.Date) }}</span>
                      <span><v-icon icon="mdi-clock-outline" size="small" class="mr-1" /> {{ formatTime24Hour(round.Time) }}</span>
                    </div>
                  </v-card-subtitle>
                  <v-card-text>
                    <div class="d-flex flex-column">
                      <div v-if="round.CourseName" class="mb-2">
                        <v-icon icon="mdi-map-marker" size="small" class="mr-1" />
                        {{ formatCourseName(round.CourseName) }}
                      </div>

                      <div v-if="hasResults(round)" class="mt-2">
                        <div class="text-subtitle-2">Top Players:</div>
                        <div v-for="(player, i) in getTopPlayers(round, 3)" :key="i" class="my-1">
                          <span class="font-weight-medium">{{ i+1 }}. {{ player.Name }}</span>
                          <span
                              :class="{
                              'text-error': player.Diff > 0,
                              'text-success': player.Diff < 0,
                              'text-grey': player.Diff === 0
                            }"
                              class="ml-2"
                          >
                            {{ formatDiff(player.Diff) }}
                          </span>
                        </div>
                      </div>

                      <div v-else class="text-grey mt-2">
                        No scores available yet
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </div>
        </template>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { computed } from 'vue'
import { useCompetitionStore } from '@/store/modules/competition'
import { formatDate, formatTime24Hour, formatCourseName, formatDiff } from '@/services/api'
import config from '@/config'

export default {
  name: 'HomeView',

  setup() {
    const competitionStore = useCompetitionStore()

    // Computed properties from store
    const competition = computed(() => competitionStore.competition)
    const subCompetitions = computed(() => {
      if (competition.value && competition.value.SubCompetitions) {
        return competition.value.SubCompetitions
      }
      return []
    })
    const loading = computed(() => competitionStore.loading)
    const error = computed(() => competitionStore.error)
    const competitionStatus = computed(() => competitionStore.competitionStatus)
    const isFutureCompetition = computed(() => competitionStore.isFutureCompetition)
    const timeUntilStart = computed(() => competitionStore.timeUntilStart)
    const divisionCounts = computed(() => competitionStore.divisionCounts)

    const formatCompetitionNameWithIcon = (name) => {
      if (!name) return '';

      // Replace &rarr; with icon, keep other HTML entities
      return name.replace(/&rarr;/g, '<v-icon icon="mdi-arrow-right" size="small" class="mx-1"></v-icon>')
          .replace(/&amp;/g, '&');
    };

    // Get competition ID from config
    const competitionId = config.api.competitionId

    // Helper function to check if a competition has results
    const hasResults = (comp) => {
      return comp && comp.Results && comp.Results.length > 0 &&
          comp.Results.some(player => player.hasStarted ||
              (player.PlayerResults && player.PlayerResults.some(hole => hole && hole.Result)))
    }

    // Get top players from a competition
    const getTopPlayers = (comp, count = 3) => {
      if (!hasResults(comp)) return []

      // Process results to ensure 'hasStarted' property
      const processedResults = comp.Results.map(result => ({
        ...result,
        hasStarted: Array.isArray(result.PlayerResults) &&
            result.PlayerResults.some(hole => hole && hole.Result)
      }))

      // Filter out players who haven't started and sort by position
      return processedResults
          .filter(player => player.hasStarted)
          .sort((a, b) => (parseInt(a.Place) || Infinity) - (parseInt(b.Place) || Infinity))
          .slice(0, count)
    }

    return {
      competition,
      subCompetitions,
      loading,
      error,
      competitionStatus,
      isFutureCompetition,
      timeUntilStart,
      divisionCounts,
      competitionId,
      formatDate,
      formatTime24Hour,
      formatCourseName,
      formatCompetitionNameWithIcon,
      formatDiff,
      hasResults,
      getTopPlayers
    }
  }
}
</script>

<style scoped>
.text-primary {
  color: #1976D2 !important;
}

.text-warning {
  color: #FB8C00 !important;
}

.text-success {
  color: #4CAF50 !important;
}

.text-info {
  color: #2196F3 !important;
}

.text-error {
  color: #F44336 !important;
}

.text-grey {
  color: #9E9E9E !important;
}
</style>
